import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const highlights = useMemo(
    () => [
      {
        icon: <PeopleAltRoundedIcon sx={{ fontSize: 22 }} />,
        title: "Gestión de miembros",
        text: "Administra registros, perfiles y accesos desde un solo panel.",
      },
      {
        icon: <TrendingUpRoundedIcon sx={{ fontSize: 22 }} />,
        title: "Control operativo",
        text: "Visualiza la actividad del sistema y centraliza procesos internos.",
      },
    ],
    []
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      general: "",
    }));

    if (banner.message) {
      setBanner({ type: "", message: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.email.trim()) {
      newErrors.email = "Ingresa tu correo electrónico";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!form.password) {
      newErrors.password = "Ingresa tu contraseña o clave temporal";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/admin/login/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo iniciar sesión";

        setErrors((prev) => ({
          ...prev,
          general: message,
        }));

        setBanner({
          type: "error",
          message,
        });

        return;
      }

      login(data.user, data.access, data.refresh);

      if (data.must_change_password) {
        navigate("/admin/change-password");
        return;
      }

      navigate("/admin/dashboard/staff");
    } catch {
      const message = "Ocurrió un error al conectar con el servidor";

      setErrors((prev) => ({
        ...prev,
        general: message,
      }));

      setBanner({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(circle at top left, rgba(229,9,20,0.18) 0%, rgba(229,9,20,0.03) 22%, transparent 42%), radial-gradient(circle at bottom right, rgba(229,9,20,0.12) 0%, rgba(229,9,20,0.03) 18%, transparent 38%), linear-gradient(135deg, #070707 0%, #0e0e11 35%, #151518 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom right, rgba(255,255,255,0.02), rgba(255,255,255,0))",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: -120,
          right: -120,
          width: 320,
          height: 320,
          borderRadius: "50%",
          bgcolor: "rgba(229,9,20,0.14)",
          filter: "blur(80px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: -140,
          left: -100,
          width: 280,
          height: 280,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.05)",
          filter: "blur(90px)",
        }}
      />

      <Container
        maxWidth={false}
        sx={{
          position: "relative",
          zIndex: 1,
          width: { xs: "92%", md: "90%", xl: "82%" },
          mx: "auto",
          py: { xs: 3, sm: 4, md: 5 },
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.08fr 0.92fr" },
            gap: { xs: 3, md: 4, lg: 5 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              position: "relative",
              borderRadius: { xs: "28px", md: "34px" },
              p: { xs: 3, sm: 3.5, md: 4.5, lg: 5.5 },
              border: "1px solid rgba(255,255,255,0.08)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.28)",
              overflow: "hidden",
            }}
          >
            <Stack spacing={{ xs: 2.5, md: 3 }}>
              <Box sx={{ maxWidth: 640 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 700,
                    lineHeight: 0.98,
                    letterSpacing: "-0.04em",
                    fontSize: {
                      xs: "clamp(38px, 5vw, 56px)",
                      md: "clamp(54px, 3vw, 78px)",
                    },
                    mb: 2,
                  }}
                >
                  Administra NovaXFit con una experiencia moderna
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: { xs: 15.5, md: 17 },
                    lineHeight: 1.8,
                    maxWidth: 560,
                  }}
                >
                  Accede al panel interno para gestionar miembros, controlar la
                  operación y mantener la administración del gimnasio.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr", xl: "1fr" },
                  gap: 1.4,
                  mt: 1,
                  maxWidth: 560,
                }}
              >
                {highlights.map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.6,
                      p: 1.8,
                      borderRadius: "22px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
                    }}
                  >
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        minWidth: 46,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ffffff",
                        background:
                          "linear-gradient(135deg, #e50914 0%, #9d0710 100%)",
                        boxShadow: "0 16px 30px rgba(229,9,20,0.22)",
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          color: "#ffffff",
                          fontWeight: 800,
                          fontSize: 15.5,
                          mb: 0.5,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.68)",
                          fontSize: 14.2,
                          lineHeight: 1.7,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                maxWidth: 520,
                mx: "auto",
                borderRadius: { xs: "28px", md: "34px" },
                p: { xs: 2.4, sm: 3, md: 3.8 },
                border: "1px solid rgba(255,255,255,0.08)",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(250,250,250,0.94) 100%)",
                boxShadow: "0 34px 90px rgba(0,0,0,0.34)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -80,
                  right: -60,
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  bgcolor: "rgba(229,9,20,0.12)",
                  filter: "blur(24px)",
                }}
              />

              <Stack spacing={2.2} sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.4,
                    mb: 0.4,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg, #111111 0%, #2a2a2a 50%, #e50914 100%)",
                      boxShadow: "0 18px 36px rgba(0,0,0,0.18)",
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon sx={{ fontSize: 28 }} />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "#111111",
                        fontSize: { xs: 24, md: 28 },
                        fontWeight: 900,
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      Iniciar sesión
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(17,17,17,0.62)",
                        fontSize: 14.5,
                        fontWeight: 600,
                      }}
                    >
                      Acceso exclusivo para administradores
                    </Typography>
                  </Box>
                </Box>

                {banner.message && (
                  <Alert
                    severity={banner.type}
                    sx={{
                      borderRadius: "16px",
                      "& .MuiAlert-message": {
                        fontWeight: 600,
                      },
                    }}
                  >
                    {banner.message}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Correo electrónico"
                  type="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  error={Boolean(errors.email)}
                  helperText={errors.email || " "}
                  variant="outlined"
                  autoComplete="email"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontWeight: 500,
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      bgcolor: "#ffffff",
                      fontWeight: 500,
                      "& fieldset": {
                        borderColor: "rgba(17,17,17,0.12)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(17,17,17,0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#e50914",
                        borderWidth: "2px",
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Contraseña o clave temporal"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange("password")}
                  error={Boolean(errors.password)}
                  helperText={errors.password || " "}
                  variant="outlined"
                  autoComplete="current-password"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontWeight: 500,
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      bgcolor: "#ffffff",
                      fontWeight: 500,
                      "& fieldset": {
                        borderColor: "rgba(17,17,17,0.12)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(17,17,17,0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#e50914",
                        borderWidth: "2px",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((prev) => !prev)}
                          sx={{
                            color: "#111111",
                            "&:hover": {
                              bgcolor: "rgba(17,17,17,0.05)",
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffRoundedIcon />
                          ) : (
                            <VisibilityRoundedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    mt: 0.4,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: "18px",
                    py: 1.45,
                    fontSize: "1rem",
                    color: "#ffffff",
                    background:
                      "linear-gradient(135deg, #0d0d0d 0%, #2a2a2a 45%, #e50914 100%)",
                    boxShadow: "0 22px 42px rgba(0,0,0,0.2)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #090909 0%, #1e1e1e 45%, #d40812 100%)",
                      boxShadow: "0 26px 48px rgba(0,0,0,0.26)",
                    },
                    "&.Mui-disabled": {
                      color: "#ffffff",
                      opacity: 0.78,
                    },
                  }}
                >
                  {loading ? "Ingresando..." : "Entrar al panel"}
                </Button>

                <Box
                  sx={{
                    mt: 0.8,
                    px: 1,
                    py: 1.2,
                    borderRadius: "16px",
                    bgcolor: "rgba(17,17,17,0.04)",
                    border: "1px solid rgba(17,17,17,0.06)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#111111",
                      fontWeight: 700,
                      fontSize: 13.5,
                      mb: 0.4,
                    }}
                  >
                    Entorno protegido
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(17,17,17,0.66)",
                      fontSize: 13.6,
                      lineHeight: 1.7,
                      fontWeight: 500,
                    }}
                  >
                    Usa únicamente tus credenciales administrativas para acceder
                    al dashboard interno del gimnasio.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}